import { useEffect, useState } from 'react';

import loadApp, { App } from './app';
import loadAppLauncher from './app-launcher';
import loadBarcodeScanner from './barcode-scanner';
import loadBiometric from './biometric';
import loadBrowser from './browser';
import loadCamera from './camera';
import loadCore from './core';
import loadDevice from './device';
import loadEdgeToEdge from './edge-to-edge';
import loadHaptics from './haptics';
import loadKeyboard from './keyboard';
import loadNetwork from './network';
import loadPreferences from './preferences';
import loadSplashScreen from './splash-screen';

type EdgeToEdgeModule = typeof import('capacitor7-android-edge-to-edge-support');
type NativeBiometricModule = typeof import('@capgo/capacitor-native-biometric');

enum CapacitorVersion {
  v8_0 = 'capacitor-v8_0',
  v7_0 = 'capacitor-v7_0',
}

// Add new versions at the top of the object. Anything not listed will be marked as deprecated.
const versionLoaders = {
  [CapacitorVersion.v8_0]: async () =>
    (await import(/* webpackChunkName: "capacitor-v8" */ './v8')).default,
  [CapacitorVersion.v7_0]: async () =>
    (await import(/* webpackChunkName: "capacitor-v7" */ './v7')).default,
};

type SupportedCapacitorVersion = keyof typeof versionLoaders;

const supportedVersions: SupportedCapacitorVersion[] = [
  CapacitorVersion.v8_0,
  CapacitorVersion.v7_0,
];

const getVersion = () => {
  // On web, window.Capacitor is undefined until capacitor is "imported" - however, on native app,
  // window.Capacitor is always present.
  // We can use this trick to detect web platform, and return the latest version.
  // Native builds include `capacitor-v*` in the userAgent, while web does not.
  if (!window.Capacitor) {
    return supportedVersions[0];
  }

  return Object.values(CapacitorVersion).find(version => navigator.userAgent.includes(version));
};

/**
 * This hook should only be called once as its required, calling it more than once can lead to performance
 * issues as it will try to load the capacitor plugins all again, to avoid the situation we throw an error
 * as soon as the hook is being called again.
 */
const useRegisterCapacitorPlugins = () => {
  const [isRegistering, setIsRegistering] = useState(true);
  const [isObsoleteVersion, setIsObsoleteVersion] = useState(false);

  useEffect(() => {
    if (App) {
      throw Error('useRegisterCapacitorPlugins should only be called once.');
    }

    const effect = async (version: SupportedCapacitorVersion) => {
      const plugins = await versionLoaders[version]();

      loadApp(plugins.App);
      loadAppLauncher(plugins.AppLauncher);
      loadBarcodeScanner(plugins.BarcodeScanner);
      loadBrowser(plugins.Browser);
      loadCamera(plugins.Camera);
      loadCore(plugins.Core);
      loadDevice(plugins.Device);
      if (version === CapacitorVersion.v7_0 && 'EdgeToEdge' in plugins) {
        loadEdgeToEdge((plugins as { EdgeToEdge: EdgeToEdgeModule }).EdgeToEdge);
      }
      loadKeyboard(plugins.Keyboard);
      loadNetwork(plugins.Network);
      loadPreferences(plugins.Preferences);
      loadSplashScreen(plugins.SplashScreen);
      loadHaptics(plugins.Haptics);
      if (version === CapacitorVersion.v8_0 && 'NativeBiometric' in plugins) {
        loadBiometric((plugins as { NativeBiometric: NativeBiometricModule }).NativeBiometric);
      } else {
        loadBiometric();
      }

      setIsRegistering(false);
    };

    const version = getVersion();
    if (version && supportedVersions.includes(version)) {
      // skipcq: JS-0328
      effect(version as SupportedCapacitorVersion);
    } else {
      setIsObsoleteVersion(true);
      // If we're forcing an incompatible version, we should load the oldest supported version.
      // skipcq: JS-0328
      effect(supportedVersions[supportedVersions.length - 1] as SupportedCapacitorVersion);
    }
  }, []);

  return {
    isRegistering,
    isObsoleteVersion,
  };
};

export default useRegisterCapacitorPlugins;
